import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function ConfirmDeleteDialog({
  onDelete,
  checkedItems,
  selectedProjects = [],
  setIsConfirmModalOpen,
}) {
  const { t, i18n } = useTranslation();
  const deleteCount = Array.isArray(checkedItems) ? checkedItems.length : 0;
  const selectedProjectList = Array.isArray(selectedProjects)
    ? selectedProjects
        .map((project) => ({
          id: project?.id ?? project?.project_id ?? project?.project_name,
          name: String(project?.project_name ?? "").trim(),
        }))
        .filter((project) => project.name)
    : [];
  const primaryProjectName = selectedProjectList[0]?.name ?? "";
  const hasMultipleProjects = selectedProjectList.length > 1;
  const isKorean = String(
    i18n.resolvedLanguage ?? i18n.language ?? "",
  ).startsWith("ko");

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsConfirmModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setIsConfirmModalOpen]);

  const handleClose = () => setIsConfirmModalOpen(false);
  const handleConfirm = () => onDelete(checkedItems);

  return (
    <div
      className="confirmDeleteDialog"
      role="presentation"
      onMouseDown={handleClose}
    >
      <div
        className="confirmDeleteCard"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="confirmDeleteClose"
          onClick={handleClose}
          aria-label={t("projectOverview.confirmDeleteDialog.closeButton")}
        >
          <span aria-hidden="true">x</span>
        </button>

        <div className="confirmDeleteTopbar">
          <span className="confirmDeleteTopSignal">
            {t("projectOverview.confirmDeleteDialog.eyebrow")}
          </span>
          <span className="confirmDeleteTopRule" aria-hidden="true" />
        </div>

        <div className="confirmDeleteHero">
          <div className="confirmDeleteIconWrap" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M12 8V13.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="12" cy="17.2" r="1.1" fill="currentColor" />
              <path
                d="M10.29 3.86L2.82 16.51C1.96 17.96 3 19.78 4.68 19.78H19.32C21 19.78 22.04 17.96 21.18 16.51L13.71 3.86C12.87 2.45 11.13 2.45 10.29 3.86Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="confirmDeleteHeroText">
            <h3 id="confirm-delete-title">
              {t("projectOverview.confirmDeleteDialog.title")}
            </h3>
            <p id="confirm-delete-description">
              {t("projectOverview.confirmDeleteDialog.description", {
                count: deleteCount,
              })}
            </p>
          </div>

          {/* <div className="confirmDeleteCountPanel">
            <span className="confirmDeleteCountPanelLabel">
              {t("projectOverview.confirmDeleteDialog.countLabel")}
            </span>
            <strong>{deleteCount}</strong>
          </div> */}
        </div>

        <div className="confirmDeleteMeta">
          <div className="confirmDeleteMetaBlock">
            <span className="confirmDeleteMetaLabel">
              {t("projectOverview.confirmDeleteDialog.countLabel")}
            </span>
            <p>{deleteCount}</p>
          </div>

          <div className="confirmDeleteMetaBlock">
            <span className="confirmDeleteMetaLabel">WARNING</span>
            <p>{t("projectOverview.confirmDeleteDialog.warning")}</p>
          </div>

          {primaryProjectName && (
            <div className="confirmDeleteMetaBlock confirmDeleteMetaBlockWide">
              <span className="confirmDeleteMetaLabel">
                {t("projectOverview.projectList.projectNameLabel")}
              </span>
              <p className="confirmDeleteProjectSummary">
                {hasMultipleProjects
                  ? t("projectOverview.confirmDeleteDialog.projectSummary", {
                      name: primaryProjectName,
                      count: selectedProjectList.length - 1,
                      defaultValue: isKorean
                        ? `${primaryProjectName} 외 ${selectedProjectList.length - 1}개`
                        : `${primaryProjectName} and ${selectedProjectList.length - 1} more`,
                    })
                  : primaryProjectName}
              </p>
              {hasMultipleProjects && (
                <details className="confirmDeleteProjectList">
                  <summary>
                    {t("projectOverview.confirmDeleteDialog.viewProjectList", {
                      count: selectedProjectList.length,
                      defaultValue: isKorean
                        ? `선택된 프로젝트 전체 보기 (${selectedProjectList.length})`
                        : `View selected projects (${selectedProjectList.length})`,
                    })}
                  </summary>
                  <ul>
                    {selectedProjectList.map((project) => (
                      <li key={project.id}>{project.name}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="confirmDeleteActions">
          <button
            type="button"
            className="confirmDeleteSecondary"
            onClick={handleClose}
          >
            {t("editorBtnBox.cancelButton")}
          </button>

          <button
            type="button"
            className="confirmDeletePrimary"
            onClick={handleConfirm}
          >
            {t("projectOverview.confirmDeleteDialog.confirmButton")}
          </button>
        </div>
      </div>
    </div>
  );
}
